import { type ComponentPropsWithoutRef, type MouseEvent } from 'react';
import { Link, matchPath, useInRouterContext } from 'react-router-dom';

import { useDocumentNavigation } from '../../context/DocumentNavigationContext';

const getDocumentId = (
  href: string | undefined,
  documentOrigin: string | undefined,
  documentPathPatterns: readonly string[],
): string | undefined => {
  if (!href || href.startsWith('#')) return undefined;
  const resourceMatch = href.match(
    /^(?:\/\/)?collaborativeDocuments\/([^/?#\s]+)$/,
  );
  if (resourceMatch) return resourceMatch[1];

  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin && url.origin !== documentOrigin)
      return undefined;
    const match = [
      '/collaborativeDocuments/:documentId',
      ...documentPathPatterns,
    ]
      .map(pattern =>
        matchPath({ path: pattern, caseSensitive: true }, url.pathname),
      )
      .find(result => result?.params.documentId);
    const id = match?.params.documentId
      ? decodeURIComponent(match.params.documentId)
      : undefined;
    return id && !/[/?#\s]/.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
};

type DocumentLinkProps = ComponentPropsWithoutRef<'a'> & {
  documentId?: string;
};

export const DocumentLink = ({
  documentId,
  href,
  onClick,
  children,
  ...props
}: DocumentLinkProps) => {
  const inRouter = useInRouterContext();
  const {
    buildDocumentHref,
    openDocument,
    documentOrigin,
    documentPathPatterns,
  } = useDocumentNavigation();
  const id =
    documentId ?? getDocumentId(href, documentOrigin, documentPathPatterns);
  const resolvedHref =
    id && (!href || /^(?:\/\/)?collaborativeDocuments\//.test(href))
      ? buildDocumentHref(id)
      : href;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      !id ||
      !openDocument ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      props.download !== undefined
    ) {
      return;
    }
    event.preventDefault();
    openDocument(id);
  };

  if (inRouter && resolvedHref && props.download === undefined) {
    try {
      const url = new URL(resolvedHref, window.location.href);
      if (url.origin === window.location.origin) {
        return (
          <Link
            data-track-source-anchor="189eef83fe;document-link" {...props}
            to={`${url.pathname}${url.search}${url.hash}`}
            onClick={handleClick}
          >
            {children}
          </Link>
        );
      }
    } catch {
      return (
        <a data-track-source-anchor="7d6027183e;document-link-a" {...props} href={resolvedHref} onClick={handleClick}>
          {children}
        </a>
      );
    }
  }

  return (
    <a data-track-source-anchor="585a19c642;document-link-a-2" {...props} href={resolvedHref} onClick={handleClick}>
      {children}
    </a>
  );
};
